import { useMemo } from 'react';
import {
    REFERRAL_ATTR_ITEM_ID,
    REFERRAL_ATTR_PROFESSIONAL_ID,
    REFERRAL_ATTR_PROFESSIONAL_TITLE,
    REFERRAL_ATTR_SOURCE,
    TRACK_REFERRAL_ATTR,
    type Tracker,
} from '@archipro-website/tracker';
import { useAppData } from '~/modules/root';
import { useIsGoodBot } from '~/modules/root/hook/use-is-good-bot';
import { decodeEntities } from '~/utils';
import { goInCurrentTab, goInNewTab } from '~/utils/urlHelper';

export interface WebsiteTrackingProps {
    websiteURL?: string | null;
    libraryItemID?: number;
    professionalID?: number;
    professionalTitle?: string | null;
}

// If we start linking to client sites, we need to update the tracking to not use this function
// since it doesn't support tracking on right or middle mouse button clicks
export const onAdClickVisitWebsite = (
    trackingProps: WebsiteTrackingProps,
    tracker: Tracker,
    event?: React.MouseEvent,
): void => {
    const {
        websiteURL,
        libraryItemID = 0,
        professionalID = 0,
        professionalTitle = '',
    } = trackingProps;

    const website = getWebsiteLink(websiteURL);

    if (!website) {
        return;
    }

    const customData = {
        LibraryItemID: libraryItemID,
        ProfessionalID: professionalID,
        ProfessionalTitle: professionalTitle,
        ExternalURL: decodeEntities(website),
        ExtraData: {
            MouseEvent: event?.button ?? 0,
        },
    };

    tracker.log('Website', {
        data: customData,
        ga4Data: customData,
        label: professionalTitle,
        url: new URL(window.location.href),
    });

    if (
        !event?.currentTarget ||
        event.currentTarget.getAttribute('target') === '_blank'
    ) {
        goInNewTab(website);
    } else {
        goInCurrentTab(website);
    }
};

// Can be removed if/once the above function is removed
const getWebsiteLink = (website?: string | null): string | null => {
    if (!website) {
        return null;
    }

    try {
        const websiteURL = decodeEntities(website);

        return enhanceProfessionalWebsiteLink(
            websiteURL,
            shouldAttachUTM(websiteURL),
        );
    } catch {
        console.error('Invalid URL');
        return null;
    }
};

/**
 * Lower-level outbound link enhancer. Prefer
 * {@link getTrackedProfessionalWebsiteLink} for referral CTAs that should
 * apply UTM + `__apid`, or {@link attachApIdToProfessionalWebsiteLink} when
 * GraphQL `WebsiteLink` has already applied DisableUTM / page-id rules.
 */
export const enhanceProfessionalWebsiteLink = (
    link: string,
    attachUTM: boolean,
    userTrackingId: string | null = null,
): string => {
    try {
        let url = new URL(link);
        if (attachUTM) url = addUtmData(url);
        if (userTrackingId) url = addApIdTracking(url, userTrackingId);
        return url.href;
    } catch {
        return link;
    }
};

/**
 * Attach UTM / `__apid` to an outbound professional website link using the
 * shared host blacklist rules.
 */
export const getTrackedProfessionalWebsiteLink = (
    websiteURL: string,
    userTrackingId: string | null,
): string => {
    return enhanceProfessionalWebsiteLink(
        websiteURL,
        shouldAttachUTM(websiteURL),
        userTrackingId,
    );
};

/**
 * Attach only `__apid` to a GraphQL `WebsiteLink`.
 * Does not re-apply UTM — `WebsiteLink` already respects DisableUTM.
 */
export const attachApIdToProfessionalWebsiteLink = (
    websiteURL: string,
    userTrackingId: string | null,
): string => {
    return enhanceProfessionalWebsiteLink(websiteURL, false, userTrackingId);
};

/**
 * Attach `__apid` to each amplifier tile's GraphQL website link.
 */
export const withAmplifierWebsiteTracking = <
    T extends { websiteLink?: string | null },
>(
    items: T[] | null | undefined,
    userTrackingId: string | null,
): T[] | null | undefined => {
    if (!items?.length) {
        return items;
    }

    return items.map((item) => ({
        ...item,
        websiteLink: item.websiteLink
            ? attachApIdToProfessionalWebsiteLink(
                  item.websiteLink,
                  userTrackingId,
              )
            : item.websiteLink,
    }));
};

/**
 * GraphQL `User.APID` sentinel when the request has no device token
 * (`User::resolveAPIDField`). Truthy, so callers must not treat it as a real id.
 */
export const UNKNOWN_USER_TRACKING_ID = 'unknown';

/**
 * True when `userTrackingId` is a real visitor device token suitable for `__apid`.
 */
export const isValidOutboundUserTrackingId = (
    userTrackingId: string | null | undefined,
): userTrackingId is string =>
    Boolean(userTrackingId) && userTrackingId !== UNKNOWN_USER_TRACKING_ID;

/**
 * Resolve the visitor `__apid` for outbound professional website links.
 * Bots and the GraphQL `unknown` APID sentinel get null so tracking ids are not attached.
 */
export const useOutboundUserTrackingId = (): string | null => {
    const selfIdentifiesAsBot = useIsGoodBot();
    const appData = useAppData();
    const userTrackingId = appData.user?.APID ?? null;

    if (selfIdentifiesAsBot || !isValidOutboundUserTrackingId(userTrackingId)) {
        return null;
    }

    return userTrackingId;
};

/**
 * Amplifier tiles with `__apid` on outbound website links (no UTM re-attach).
 */
export const useTrackedAmplifiedItems = <
    T extends { websiteLink?: string | null },
>(
    items: T[] | null | undefined,
): T[] | null | undefined => {
    const userTrackingId = useOutboundUserTrackingId();

    return useMemo(
        () => withAmplifierWebsiteTracking(items, userTrackingId),
        [items, userTrackingId],
    );
};

/**
 * Hook to provide props for external links to enable tracking events. Links
 * should add the returned params as attributes to enable tracking.
 * @param websiteURL The website URL to track.
 * @param inputParams Tracking metadata.
 * @see packages/tracker/src/react-bindings/hooks/useLogLinkClicks.ts
 */
export const useWebsiteReferralTracking = (
    websiteURL: string | null | undefined,
    inputParams: {
        libraryItemID: number;
        professionalID: number;
        professionalTitle: string;
        /** Which CTA surface renders the link; lands as Source on the Website event. */
        source?: string;
    },
) => {
    const userTrackingId = useOutboundUserTrackingId();

    // Professionals manage the Website field themselves, so it is a trust
    // boundary: a value that isn't parseable http(s) (missing scheme, or a
    // non-web scheme like javascript:) must not reach an href — and an
    // unparseable value must not throw on the render path.
    const parsedWebsite = websiteURL ? safeParseHttpUrl(websiteURL) : null;

    if (!websiteURL || !parsedWebsite) {
        return {
            referralTrackingURL: undefined,
            referralTrackingParams: {},
        } as const;
    }

    const trackedWebsite = getTrackedProfessionalWebsiteLink(
        websiteURL,
        userTrackingId,
    );

    return {
        referralTrackingURL: trackedWebsite,
        referralTrackingParams: {
            [TRACK_REFERRAL_ATTR]: '1',
            [REFERRAL_ATTR_ITEM_ID]: inputParams.libraryItemID,
            [REFERRAL_ATTR_PROFESSIONAL_ID]: inputParams.professionalID,
            [REFERRAL_ATTR_PROFESSIONAL_TITLE]: inputParams.professionalTitle,
            ...(inputParams.source && {
                [REFERRAL_ATTR_SOURCE]: inputParams.source,
            }),
            rel: 'noopener',
        },
    } as const;
};

const addApIdTracking = (url: URL, userTrackingId: string): URL => {
    if (shouldTrackUrl(url)) {
        // `set` overwrites any existing `__apid` (e.g. pasted on a professional
        // Website). Pixel reads the first occurrence via searchParams.get.
        url.searchParams.set('__apid', userTrackingId);
    }

    return url;
};

type UTMParam = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content';

export const addUtmData = (
    url: URL,
    utmData: Record<UTMParam, string> = {
        utm_source: 'archipro',
        utm_medium: 'website',
        utm_campaign: 'website',
        utm_content: '',
    },
): URL => {
    Object.keys(utmData).forEach((key) => {
        const value = utmData[key as UTMParam];
        if (!url.searchParams.has(key) && value) {
            url.searchParams.append(key, value);
        }
    });
    return url;
};

/***
 * super hacky hotfix to stop __apid to be added for provista
 * since it breaks the destination website
 * @param url
 */
const shouldTrackUrl = (url: URL): boolean => {
    const blacklist = [
        'clivebarrington.com',
        'fgrarchitects.com.au',
        'saniflo.co.nz',
        'dormakaba.com',
    ];

    const hostname = url.host.replace(/^www\./, '');

    return !blacklist.includes(hostname);
};

/**
 * Parse a professional-supplied website value, accepting only http(s). Returns
 * null for unparseable values or non-web schemes instead of throwing — the
 * value is user-managed CMS data on a render path.
 */
const safeParseHttpUrl = (url: string): URL | null => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
            ? parsed
            : null;
    } catch {
        return null;
    }
};

/**
 * super hax for blacklisting clients sites that
 * cannot handle query paramaters at all.
 */
const shouldAttachUTM = (url: string): boolean => {
    const host = safeParseHttpUrl(url)?.host;
    const blacklist = ['clivebarrington.com'];
    return host ? !blacklist.includes(host) : false;
};
